//src/factory/symbols/createBellSymbols.js
import * as THREE from 'three';
import { generateUniqueId } from '../../utils/generateUniqueId';

 function createCircle(radius, segments, material) {
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const theta = (i / segments) * Math.PI * 2;
    points.push(new THREE.Vector3(
      Math.cos(theta) * radius,
      Math.sin(theta) * radius,
      0
    ));
  }
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  return new THREE.LineLoop(geometry, material);
}

export function createBellTrafo(scene, position, color = 0x0000ff, isPreview = false, dir = new THREE.Vector3(1,0,0)) {
  const group = new THREE.Group();
  const trafoId = generateUniqueId('bellTrafo');

  group.userData = {
    id: trafoId,
    type: 'bellTrafo',
    isSelectable: true,
    isPreview: isPreview,
    basePoint: { x: position.x, y: position.y }
  };

  group.position.set(position.x, position.y, 0);

  // Use provided color when available so preview matches settings-derived color.
  const trafoColor = (typeof color !== 'undefined' && color !== null) ? color : (isPreview ? 0xffaa00 : 0x0000ff);
  const material = new THREE.LineBasicMaterial({
    color: trafoColor,
    transparent: isPreview,
    opacity: isPreview ? 0.5 : 1
  });

  const radius = 5;
  const segments = 64;

  // İki daire
  const circle1 = createCircle(radius, segments, material);
  circle1.position.x = radius * 2;
  group.add(circle1);

  const circle2 = createCircle(radius, segments, material);
  circle2.position.x = radius * 3;
  group.add(circle2);

  // Yatay çizgi
  const lineGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(1 * radius, 0, 0)
  ]);
  const line = new THREE.Line(lineGeo, material);
  group.add(line);

  const lineGeo2 = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(4 * radius, 0, 0),
    new THREE.Vector3(5 * radius, 0, 0)
  ]);
  const line2 = new THREE.Line(lineGeo2, material);
  group.add(line2);

  // ✅ Çıkış noktası
  const outputPoint = new THREE.Vector3(5 * radius, 0, 0);
  const connectionPointMesh = new THREE.Mesh(
    new THREE.CircleGeometry(1, 16),
    new THREE.MeshBasicMaterial({ color: 0xff0000, visible: false })
  );
  connectionPointMesh.position.copy(outputPoint);
  connectionPointMesh.userData = { type: 'trafoOutput', trafoId };
  group.add(connectionPointMesh);

  group.userData.outputPoint = outputPoint;
  group.userData.outputMarker = connectionPointMesh;

  // ✅ Yeni ekleme: dir yönüne göre döndür
  if (dir instanceof THREE.Vector3) {
    const angle = Math.atan2(dir.y, dir.x);
    group.rotation.z = angle; // artık dir yönünde bakar
  }

  if (scene && !isPreview) {
    scene.add(group);
  }

  // Eğer dir verilmişse, z ekseninde döndür (önizlemedeki davranışla eşleştir)
  if (dir instanceof THREE.Vector3) {
    const angle = Math.atan2(dir.y, dir.x);
    // Bell yüzü kabloya dik olacak şekilde - önizlemede de angle - PI/2 kullanılıyor
    group.rotation.z = angle - Math.PI / 2;
  } else if (typeof dir === 'number') {
    // Eğer scalar verilmişse (legacy), doğrudan y-ekseni skalası veya açı uygulanabilir
    try {
      group.rotation.z = dir;
    } catch (e) {
      // ignore
    }
  }

  return group;
}

 

/**
 * Zil sembolü oluşturur
 */
 

export function createBell(scene, position, color = 0xff3400, isPreview = false, dir = null) {
  const group = new THREE.Group();
  const bellId = generateUniqueId('bell');

  group.userData = {
    id: bellId,
    type: 'bell',
    isSelectable: true,
    isPreview: isPreview,
    basePoint: { x: position.x, y: position.y },
    power: 5 // Zil güç tüketimi (W)
  };

  group.position.set(position.x, position.y, 0);

  // Use provided color when available so bell preview matches settings-derived color.
  const bellColor = (typeof color !== 'undefined' && color !== null) ? color : (isPreview ? 0x0045ff : 0xff3400);
  const material = new THREE.LineBasicMaterial({
    color: bellColor,
    transparent: isPreview,
    opacity: isPreview ? 0.5 : 1
  });

  // === 1. Yarım daire ===
  function createHalfCircle(radius, segments) {
    const points = [];
    for (let i = 0; i <= segments; i++) {
      const theta = Math.PI * (i / segments); // 0 → PI (yarım daire)
      points.push(new THREE.Vector3(
        Math.cos(theta) * radius,
        Math.sin(theta) * radius,
        0
      ));
    }
    // Üst kenar (yarım dairenin kapanması için çizgi)
    points.push(new THREE.Vector3(radius, 0, 0));
    points.push(new THREE.Vector3(-radius, 0, 0));

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    return new THREE.Line(geometry, material);
  }

  const radius = 6;
  const halfCircle = createHalfCircle(radius, 32);
  group.add(halfCircle);

  // === 2. İç titreşim çizgileri ===
  const innerLine1 = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-3, 2.5, 0),
    new THREE.Vector3(-1, 5, 0),
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(3, 2.5, 0)
  ]);
  const line1 = new THREE.Line(innerLine1, material);
  group.add(line1);

  if (scene && !isPreview) {
    scene.add(group);
  }

  return group;
}

// Note: If `dir` is provided as a THREE.Vector3, the bell will be rotated so its face is
// perpendicular to that direction (same convention as createBellTrafo/createBellButton).

/**
 * Zil butonu sembolü oluşturur
 */
export function createBellButton(scene, position, color = 0xff3400, isPreview = false, dir = 1) {
  const group = new THREE.Group();
  const offsetGroup = new THREE.Group();
  const buttonId = generateUniqueId('bellButton');

  dir = dir ?? 1; // Default değer kontrolü

  group.userData = {
    id: buttonId,
    type: 'bellButton',
    isSelectable: true,
    isPreview: isPreview,
    basePoint: { x: position.x, y: position.y },
    dir: dir // ✅ Direction'ı userData'ya kaydet (scalar veya vector)
  };

  group.position.set(position.x, position.y, 0);
  group.add(offsetGroup);
  
  const btnColor = (typeof color !== 'undefined' && color !== null) ? color : (isPreview ? 0x0045ff : 0xff3400);
  const material = new THREE.LineBasicMaterial({
    color: btnColor,
    transparent: isPreview,
    opacity: isPreview ? 0.5 : 1
  });

  // Kare (buton çerçevesi)
  const boxGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, -4, 0),
    new THREE.Vector3(8, -4, 0),
    new THREE.Vector3(8, 4, 0),
    new THREE.Vector3(0, 4, 0),
    new THREE.Vector3(0, -4, 0)
  ]);
  const box = new THREE.Line(boxGeo, material);
  offsetGroup.add(box);

  // İç küçük daire (buton göstergesi)
  const circlePoints = [];
  const circleRadius = 2;
  for (let i = 0; i <= 32; i++) {
    const theta = (i / 32) * Math.PI * 2;
    circlePoints.push(new THREE.Vector3(
      Math.cos(theta) * circleRadius + 4,
      Math.sin(theta) * circleRadius,
      0
    ));
  }
  const circleGeo = new THREE.BufferGeometry().setFromPoints(circlePoints);
  const circle = new THREE.LineLoop(circleGeo, material);
  offsetGroup.add(circle);

  // ✅ Socket gibi direction kontrolü
  if (typeof dir === 'number') {
    // Scalar ise Y ekseninde scale (mirror)
    offsetGroup.scale.set(1, dir, 1);
  }

  // ✅ Vector ise rotation uygula
  if (dir instanceof THREE.Vector3) {
    const angle = Math.atan2(dir.y, dir.x);
    group.rotation.z = angle;
  }

  if (scene && !isPreview) {
    scene.add(group);
  }

  return group;
}

export function createBell2(scene, position, color = 0x00ff00, isPreview = false) {
  const group = new THREE.Group();
  const bellId = generateUniqueId('bell');

  group.userData = {
    id: bellId,
    type: 'bell',
    isSelectable: true,
    isPreview: isPreview,
    basePoint: { x: position.x, y: position.y },
    power: 5 // Zil güç tüketimi (W)
  };

  group.position.set(position.x, position.y, 0);

  const material = new THREE.LineBasicMaterial({
    color: isPreview ? 0xffaa00 : color,
    transparent: isPreview,
    opacity: isPreview ? 0.5 : 1
  });

  // Zil sembolü - küçük kutu
  const boxGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-5, -5, 0),
    new THREE.Vector3(5, -5, 0),
    new THREE.Vector3(5, 5, 0),
    new THREE.Vector3(-5, 5, 0),
    new THREE.Vector3(-5, -5, 0)
  ]);
  const box = new THREE.Line(boxGeo, material);
  box.userData = { role: 'bellBox', bellId };
  group.add(box);

  // Çarpı işareti (zil çanı göstergesi)
  const crossGeo1 = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-3, -3, 0),
    new THREE.Vector3(3, 3, 0)
  ]);
  const cross1 = new THREE.Line(crossGeo1, material);
  cross1.userData = { role: 'bellCross', bellId };
  group.add(cross1);

  const crossGeo2 = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-3, 3, 0),
    new THREE.Vector3(3, -3, 0)
  ]);
  const cross2 = new THREE.Line(crossGeo2, material);
  cross2.userData = { role: 'bellCross', bellId };
  group.add(cross2);

  if (scene && !isPreview) {
    scene.add(group);
  }

  return group;
}

/**
 * Zil buatı - daire
 */
export function singleCircleBuatForBell(scene, point, color = 0x00ff00, uuid) {
  const createFilledCircle = (radius, color) => {
    const geometry = new THREE.CircleGeometry(radius, 64);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };

  const buatCircle = createFilledCircle(2, color);
  buatCircle.userData = {
    type: 'buatCircle',
    bellUUID: uuid,
    isSelectable: true,
    isDraggable: true
  };
  buatCircle.position.set(point.x, point.y, 0.2);

  if (scene) {
    scene.add(buatCircle);
  }

  return buatCircle;
}
